import { useState } from 'react'

import { asFailure, type Failure } from '../api/request'
import { ASCIIDOC, MARKDOWN, bare, nameOfMarkup, rememberMarkup } from './markup'
import { Select } from './Select'
import { Failed } from './Status'

/**
 * Which markup a text is written in, and the way to another one.
 *
 * **Switching costs nothing and is undone by switching back.** The markup is a label: it decides how
 * the text is drawn and, for a document, what extension its file carries. Not a byte of the text
 * moves, no version is added, and there is nothing to warn about — so this asks nothing and offers
 * both directions, as often as anybody likes.
 *
 * It is a <<Select>> and not a row of chips, because picking one of several is its own question. That
 * also means a third language costs a line in `markup.ts` and nothing here.
 */
export interface MarkupChoiceProps {
  /** What this text is written in now, as a media type. */
  readonly format: string
  /** Writes the new markup down. The text is not its business — there is nothing to send. */
  readonly onChoose: (format: string) => Promise<void>
  /**
   * Whether the choice is remembered as what the next new thing is written in.
   *
   * The notepad does: a note is made by typing into it, so there is no moment at which anybody could
   * be asked. The library does not — a document is made by a control that offers the languages
   * itself.
   */
  readonly remember?: boolean
  /** Nothing can be changed. What it says stays readable — that is half the point of it. */
  readonly readOnly?: boolean
  /** Out of the ordinary Tab sequence, without being hidden. See {@link SelectProps.tabIndex}. */
  readonly tabIndex?: number
}

export function MarkupChoice({
  format,
  onChoose,
  remember = false,
  readOnly = false,
  tabIndex,
}: MarkupChoiceProps) {
  const [working, setWorking] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  return (
    <div className="markup">
      <Select
        label="Markup"
        labelHidden
        tabIndex={tabIndex}
        value={bare(format)}
        disabled={readOnly || working}
        options={[
          { value: MARKDOWN, label: nameOfMarkup(MARKDOWN) },
          { value: ASCIIDOC, label: nameOfMarkup(ASCIIDOC) },
        ]}
        onChange={(wanted) => {
          void (async () => {
            setWorking(true)
            try {
              await onChoose(wanted)
              if (remember) {
                rememberMarkup(wanted)
              }
              setFailure(null)
            } catch (error) {
              setFailure(asFailure(error))
            } finally {
              setWorking(false)
            }
          })()
        }}
      />
      {failure !== null && <Failed failure={failure} />}
    </div>
  )
}
